import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../lib/api";

// Simple module-level cache — zones rarely change, no need to refetch per
// component mount. invalidateZonesCache() after an admin edit.
let _cache = null;
let _inflight = null;

export function invalidateZonesCache() {
  _cache = null;
}

async function fetchZones() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = apiClient.get("/zones").then(({ data }) => {
    _cache = Array.isArray(data) ? data : [];
    _inflight = null;
    return _cache;
  }).catch(() => {
    _inflight = null;
    return [];
  });
  return _inflight;
}

const ZIP_RE = /\b(\d{5})(?:-\d{4})?\b/;

export function extractZip(address) {
  if (!address) return null;
  const m = ZIP_RE.exec(address);
  return m ? m[1] : null;
}

/** Shared hook: loads the zone list once, exposes zone-for-zip/address lookups. */
export function useZones() {
  const [zones, setZones] = useState(_cache || []);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    let alive = true;
    fetchZones().then((z) => { if (alive) { setZones(z); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  const zoneForZip = useCallback((zip) => {
    if (!zip) return null;
    for (const z of zones) {
      if ((z.zip_codes || []).includes(zip)) return z;
    }
    return null;
  }, [zones]);

  const zoneForAddress = useCallback((address) => zoneForZip(extractZip(address)), [zoneForZip]);

  return { zones, loading, zoneForZip, zoneForAddress };
}
